import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { findLeftIndex, findClosestPoint, getTimezoneLabel } from "../../utils/utility";
import { escapeHtml } from "../../utils/apiValidation";
import { CHART_COLORS, SERIES_CONFIG } from "./constants";

dayjs.extend(utc);
dayjs.extend(tz);

// Format date for tooltip header
export const formatTooltipDate = (timestamp, siteTimezone) => {
  const d = siteTimezone ? dayjs(timestamp).tz(siteTimezone) : dayjs(timestamp);
  return d.format("D MMM YYYY");
};

export const formatTooltipTime = (timestamp, siteTimezone) => {
  const d = siteTimezone ? dayjs(timestamp).tz(siteTimezone) : dayjs(timestamp);
  return d.format("HH:mm:ss");
};

export const buildTooltipHTML = (x, allSeriesData, visibilityMap, siteTimezone) => {
  const header = `<div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid ${CHART_COLORS.gridLines};">
    <div style="font-size: 14px; font-weight: 600; color: ${CHART_COLORS.textPrimary};">${formatTooltipDate(x, siteTimezone)}</div>
    <div style="font-size: 12px; color: ${CHART_COLORS.textSecondary};">${formatTooltipTime(x, siteTimezone)}</div>
  </div>`;

  // Aggregate system metrics across setpoint series
  let siteProduction = 0;
  let chargeLevel = 0;
  let batteryCapacityUsed = 0;
  let siteConsumption = 0;

  SERIES_CONFIG.filter(s => s.isSetpoint).forEach(seriesCfg => {
    const data = allSeriesData[seriesCfg.key];
    if (!data || !data.length) return;
    if (visibilityMap && visibilityMap[seriesCfg.name] === false) return;

    const firstX = data[0][0];
    const lastX = data[data.length - 1][0];
    if (x < firstX || x > lastX) return;

    const leftIndex = findLeftIndex(data, x);
    if (leftIndex < 0) return;

    const metrics = data[leftIndex][2];
    if (!metrics) return;

    siteProduction += metrics.siteProduction ?? 0;
    chargeLevel += metrics.chargeLevel ?? 0;
    batteryCapacityUsed += metrics.batteryCapacityUsed ?? 0;
    siteConsumption += metrics.siteConsumption ?? 0;
  });

  const metaBlock =
    siteProduction > 0 || chargeLevel > 0 || batteryCapacityUsed > 0 || siteConsumption > 0
      ? `
    <div style="margin-top: 8px; padding: 8px; background: ${CHART_COLORS.gridBackground}; border-radius: 6px; font-size: 11px;">
      <div style="font-weight: 600; margin-bottom: 4px; color: ${CHART_COLORS.textPrimary};">System Metrics:</div>
      <div style="display: grid; grid-template-columns: auto 1fr; gap: 2px;">
        <span style="color: ${CHART_COLORS.textSecondary};">Site Production:</span>
        <span style="font-weight: 500; color: ${CHART_COLORS.textPrimary}; text-align: right;">${siteProduction.toLocaleString()} W</span>
        <span style="color: ${CHART_COLORS.textSecondary};">Charge Level:</span>
        <span style="font-weight: 500; color: ${CHART_COLORS.textPrimary}; text-align: right;">${chargeLevel.toLocaleString()} A</span>
        <span style="color: ${CHART_COLORS.textSecondary};">Battery Used:</span>
        <span style="font-weight: 500; color: ${CHART_COLORS.textPrimary}; text-align: right;">${batteryCapacityUsed.toLocaleString()} W</span>
        <span style="color: ${CHART_COLORS.textSecondary};">Net Consumption:</span>
        <span style="font-weight: 500; color: ${CHART_COLORS.textPrimary}; text-align: right;">${siteConsumption.toLocaleString()} W</span>
      </div>
    </div>
  `
      : "";

  // Series value rows
  const body = SERIES_CONFIG.map(seriesCfg => {
    if (visibilityMap && visibilityMap[seriesCfg.name] === false) return "";
    const data = allSeriesData[seriesCfg.key];
    if (!data) return "";

    let displayValue = "0 W";

    if (seriesCfg.isSetpoint) {
      const leftIndex = findLeftIndex(data, x);
      if (leftIndex >= 0 && data[leftIndex][1] !== null && data[leftIndex][1] !== undefined) {
        displayValue = `${data[leftIndex][1].toLocaleString()} W`;
      }
    } else {
      const closest = findClosestPoint(data, x);
      if (closest && closest[1] !== null && closest[1] !== undefined) {
        displayValue = `${closest[1].toLocaleString()} W`;
      }
    }

    return `
      <div style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
        <div style="display: flex; align-items: center;">
          <div style="width: 12px; height: 12px; background: ${seriesCfg.color}; border-radius: 2px; margin-right: 8px;"></div>
          <span style="color: ${CHART_COLORS.textSecondary}; font-size: 12px;">${escapeHtml(seriesCfg.name)}:</span>
        </div>
        <span style="font-weight: 600; color: ${seriesCfg.color}; font-size: 12px;">${escapeHtml(displayValue)}</span>
      </div>
    `;
  }).join("");

  return `<div style="padding: 12px; min-width: 200px;">${header}${metaBlock}<div style="margin-top: 8px;">${body}</div></div>`;
};

// Shared helper: build export content string for all series data
const buildExportContent = (allSeriesData, delimiter, siteTimezone) => {
  const tsSet = new Set();
  SERIES_CONFIG.forEach(cfg => {
    const data = allSeriesData[cfg.key];
    if (data) data.forEach(d => tsSet.add(d[0]));
  });
  const timestamps = Array.from(tsSet).sort((a, b) => a - b);

  const tzLabel = getTimezoneLabel(siteTimezone);
  const headers = [`Time (${tzLabel})`, ...SERIES_CONFIG.map(s => s.name)];
  const rows = timestamps.map(ts => {
    const d = siteTimezone ? dayjs(ts).tz(siteTimezone) : dayjs(ts);
    const row = [d.format("YYYY-MM-DD HH:mm:ss")];
    SERIES_CONFIG.forEach(cfg => {
      const data = allSeriesData[cfg.key];
      if (!data) {
        row.push("");
        return;
      }
      const point = data.find(d => d[0] === ts);
      row.push(point ? (point[1] ?? "") : "");
    });
    return row.join(delimiter);
  });

  return [headers.join(delimiter), ...rows].join("\n");
};

// CSV generation from chart data
export const generateCSV = (allSeriesData, siteTimezone) => {
  return buildExportContent(allSeriesData, ",", siteTimezone);
};

// XLS (TSV) export
export const downloadXLS = (allSeriesData, siteTimezone) => {
  const content = buildExportContent(allSeriesData, "\t", siteTimezone);
  const blob = new Blob([content], { type: "application/vnd.ms-excel" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "chart.xls";
  a.click();
  URL.revokeObjectURL(a.href);
};
